import { saveAs } from 'file-saver';
import { isEmpty } from '../helpers/util';
import ENV_CONFIG from '../env';
import * as Sentry from '@sentry/browser';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN
});

class ApiClient {
  constructor() {
    this.authHeaders = {};
    this.methodsJson = ['PUT', 'POST', 'DELETE', 'PATCH'];
    this.shutdownCallbacks = [];
    this.errorCallbacks = [];
  }

  checkStatus(response) {
    this.ingestAuthHeaders(response);
    if (response.status >= 200 && response.status < 300) {
      return response;
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
      // handle error here and call to the user messaging library
      // ie Error ({devMessage:'401 url  + payload ' , userMessage 'Oops something went wrong retry in  a few seconds '} }
      // also figure out what to do in case of 401 / token expiration.
      // my opinion is to log out the user for inactivity.
    }
  }

  static parseJSON(response) {
    return response.json();
  }

  static fullUrl(part) {
    const base = `${ENV_CONFIG.REACT_APP_SCHEMA}://${ENV_CONFIG.REACT_APP_API_HOST}`;
    return `${base}${part}`;
  }

  ingestAuthHeaders(response) {
    if (response.status === 403 || response.status_text === 'Forbidden') {
      this.shutdown();
      return;
    }

    const client = response.headers.get('client');
    const uid = response.headers.get('uid');
    const accessToken = response.headers.get('access-token');
    const expiry = response.headers.get('expiry');

    const currentHeaders = { ...this.authHeaders };
    this.authHeaders = {
      client:
        client && client !== currentHeaders.client
          ? client
          : currentHeaders.client,
      uid: uid && uid !== currentHeaders.uid ? uid : currentHeaders.uid,
      accessToken:
        accessToken && accessToken !== currentHeaders.accessToken
          ? accessToken
          : currentHeaders.accessToken,
      expiry:
        expiry && expiry !== currentHeaders.expiry
          ? expiry
          : currentHeaders.expiry
    };

    localStorage.setItem('authHeaders', JSON.stringify(this.authHeaders));
  }

  shutdown() {
    this.authHeaders = {};
    localStorage.removeItem('authHeaders');
    this.shutdownCallbacks.map(cb => cb());
  }

  registerShutdownCallback(callback) {
    this.shutdownCallbacks.push(callback);
  }

  registerErrorCallback(callback) {
    this.errorCallbacks.push(callback);
  }

  request(endpoint, method, auth, data) {
    let url = ApiClient.fullUrl(endpoint); // maybe use a proxy instead
    const headers = this.getAuthHeader(auth, method);

    const params = {
      method,
      headers,
      redirect: 'error',
      cache: 'no-cache'
    };

    params.body = this.methodsJson.includes(method)
      ? JSON.stringify(data)
      : undefined;
    url = data && method === 'GET' ? url + this.buildUrlParams(data) : url;

    // For debug
    // console.log(params);
    // console.log(url);
    this.fakeDishDebug(data);

    return (
      fetch(url, params)
        .then(response => this.checkStatus(response))
        .then(ApiClient.parseJSON)
        // .then(ApiClient.getData)
        .catch(error => this.handleError(error))
    ); // this can be deferred to the component level ? maybe
  }

  download(filename, endpoint, method, auth, data) {
    let url = ApiClient.fullUrl(endpoint); // maybe use a proxy instead
    const headers = this.getAuthHeader(auth, method);

    const params = {
      method,
      headers,
      redirect: 'error',
      cache: 'no-cache'
    };

    params.body = this.methodsJson.includes(method)
      ? JSON.stringify(data)
      : undefined;
    url = data && method === 'GET' ? url + this.buildUrlParams(data) : url;

    return fetch(url, params)
      .then(response => this.checkStatus(response))
      .then(response => this.saveAs(response, filename))
      .catch(error => this.handleError(error));
  }

  upload(endpoint, body) {
    let url = ApiClient.fullUrl(endpoint); // maybe use a proxy instead
    const headers = this.getAuthHeader(true, 'POST', false);

    const params = {
      method: 'POST',
      headers,
      body,
      redirect: 'error',
      cache: 'no-cache'
    };

    // For debug
    // console.log(params);
    // console.log(url);

    return fetch(url, params)
      .then(response => this.checkStatus(response))
      .then(ApiClient.parseJSON)
      .then(ApiClient.getData)
      .catch(error => this.handleError(error)); // this can be deferred to the component level ? maybe
  }

  saveAs(response, filename) {
    response.blob().then(blob => saveAs(blob, filename));
  }

  // goes away with json api
  static getData(json) {
    console.log(json);
    let returnData;

    // TODO this is a hack until we transition the API to go without json API
    if (json.data !== undefined) {
      returnData = json.data.attributes;
      if (json.included) {
        Object.assign(
          returnData,
          ApiClient.getIncludedRelationships(json.included)
        );
      }
    } else {
      returnData = json;
    }
    return returnData;
  }

  // goes away with json api
  static getIncludedRelationships(data) {
    let returnObject = {};
    // in the jsonapi standard associated object are returned in the 'included' attribute
    data.forEach(dataObject => {
      // if the attribute we are trying to add already exists, lets make the attribute an array
      // and push the new value and transfer the intitial value
      if (
        returnObject[dataObject.type] &&
        !Array.isArray(returnObject[dataObject.type])
      ) {
        let temp = returnObject[dataObject.type];
        returnObject[dataObject.type] = [];
        returnObject[dataObject.type].push(temp, dataObject.attributes);
      }
      // if it's already an array, just push the new value...
      else if (Array.isArray(returnObject[dataObject.type])) {
        returnObject[dataObject.type].push(dataObject.attributes);
      }
      // otherwise let's add the new attribute
      else {
        returnObject[dataObject.type] = dataObject.attributes;
      }
    });
    return returnObject;
  }

  handleError(error) {
    // do something meaningful with the error
    Sentry.captureException(error);
    console.log(error);
    this.errorCallbacks.map(cb => cb(error));
    const errors = error.response.json();
    return errors.then(({ errors, messages }) => {
      throw new Error(JSON.stringify({ errors, messages })); // display a message for user, dev, & http error code. Maybe in another helper ?
    });
  }

  buildUrlParams(data) {
    const keys = Object.keys(data);
    if (keys.length <= 0) return '';

    const keyPairs = keys.map(key => `${key}=${data[key]}`).join('&');
    return `?${keyPairs}`;
  }

  getAuthHeader(auth, method, json = true) {
    let headers = new Headers();
    if (json) headers.set('Content-Type', 'application/json');

    if (this.methodsJson.includes(method))
      headers.append('Accept', 'application/json');

    if (auth) {
      if (isEmpty(this.authHeaders)) {
        // if we don't have the access tokens
        this.authHeaders = JSON.parse(localStorage.getItem(`authHeaders`)); // clear this on logout...
        // logout + redirect to login + push to history if we still don't have the headers
      }
      if (!isEmpty(this.authHeaders)) {
        headers.set('access-token', this.authHeaders.accessToken);
        headers.set('expiry', this.authHeaders.expiry);
        headers.set('uid', this.authHeaders.uid);
        headers.set('client', this.authHeaders.client);

        if ('reset_password' in this.authHeaders)
          headers.set('reset_password', this.authHeaders.reset_password);
        if ('token' in this.authHeaders)
          headers.set('token', this.authHeaders.token);
      }
    }
    return headers;
  }

  async paginatedRequest(endpoint, method, auth, data, key) {
    const perPage = 100;
    let totalPages = 1;
    let page = 1;
    let results = [];
    do {
      let response = await this.request(
        endpoint,
        method,
        auth,
        { per_page: perPage, page, ...data }
      );
      totalPages = response.pagination.total_pages;
      results = results.concat(response[key]);
      page += 1;
    } while (page <= totalPages);
    return results;
  }

  fakeDishDebug(data) {
    if (data
      && data.menu_selections
      && data.menu_selections.length === 1
      && data.menu_selections[0].dish_id === 48148) {
        try {
          // capture stack trace
          throw new Error(`'FAKE DISH' set as only menu_selection. Data: ${JSON.stringify(data)}`)
        } catch(error) {
          Sentry.captureException(error);
        }
    }
  }
}
export const apiClient = new ApiClient();



// WEBPACK FOOTER //
// ./src/InterfaceAPI/apiClient.js